import { z } from 'zod';
/**
 * Data types that can have timestamps associated with them.
 * Mirrors the Prisma `MeetingEventTimestampDataType` enum.
 *
 * Defined as `as const` rather than a TS enum so the values are plain
 * string literals, which are directly assignable to/from Prisma's
 * generated enum type without requiring explicit casts.
 */
export const MeetingEventTimestampDataType = {
    AgendaSection: 'agenda_section',
    AgendaSectionItem: 'agenda_section_item',
    MinutesMotion: 'minutes_motion',
    MinutesMotionVote: 'minutes_motion_vote',
    MinutesNote: 'minutes_note',
    RollCall: 'roll_call',
    RollCallStatus: 'roll_call_status',
    Speaker: 'speaker',
};
const dataTypeSchema = z.nativeEnum(MeetingEventTimestampDataType);
/**
 * Schema for creating a meeting event timestamp.
 *
 * Supports two modes:
 * - **Linked mode**: `dataType` and `dataId` reference a specific record.
 * - **Label-only mode**: Both are null and `label` describes the timestamp.
 */
export const meetingEventTimestampCreateSchema = z
    .object({
    mediaArchiveId: z.number().int().positive('Media archive ID is required'),
    dataType: dataTypeSchema.nullable().optional(),
    dataId: z.number().int().positive().nullable().optional(),
    minutesEntryId: z.number().int().positive().nullable().optional(),
    timestampSeconds: z.number().nonnegative('Timestamp must be 0 or greater'),
    label: z.string().nullable().optional(),
})
    .superRefine((data, ctx) => {
    const hasDataType = data.dataType !== null && data.dataType !== undefined;
    const hasDataId = data.dataId !== null && data.dataId !== undefined;
    const hasMinutesEntryId = data.minutesEntryId !== null && data.minutesEntryId !== undefined;
    if (hasDataType !== hasDataId) {
        ctx.addIssue({
            code: 'custom',
            message: 'dataType and dataId must both be provided or both be null',
            path: hasDataType ? ['dataId'] : ['dataType'],
        });
        return;
    }
    if (!hasDataType && !hasDataId && !hasMinutesEntryId) {
        if (!data.label || data.label.trim().length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'Label is required when dataType, dataId, and minutesEntryId are not provided',
                path: ['label'],
            });
        }
    }
})
    .describe('Creates a meeting event timestamp. Supports three modes: ' +
    '(1) Linked — dataType and dataId reference a specific record and must both be provided, ' +
    '(2) Entry-level — minutesEntryId references a minutes entry (dataType/dataId may be null), ' +
    '(3) Label-only — all reference fields are null and a non-empty label is required.');
/**
 * Schema for bulk-creating meeting event timestamps.
 *
 * Accepts an array of create inputs. Each item follows the same
 * validation rules as `meetingEventTimestampCreateSchema`.
 */
export const meetingEventTimestampBulkCreateSchema = z.object({
    timestamps: z
        .array(meetingEventTimestampCreateSchema)
        .min(1, 'At least one timestamp is required'),
});
const updateFields = z.object({
    timestampSeconds: z.number().nonnegative('Timestamp must be 0 or greater').optional(),
    label: z.string().trim().min(1, 'Label must not be empty').nullable().optional(),
});
const requireAtLeastOneField = (data) => data.timestampSeconds !== undefined || data.label !== undefined;
const atLeastOneFieldMessage = {
    message: 'At least one of timestampSeconds or label must be provided',
};
/** Schema for updating a meeting event timestamp. At least one field is required. */
export const meetingEventTimestampUpdateSchema = updateFields.refine(requireAtLeastOneField, atLeastOneFieldMessage);
/** Schema for a single item in a bulk update request */
const bulkUpdateItemSchema = updateFields
    .extend({ id: z.number().int().positive('Timestamp ID is required') })
    .refine(requireAtLeastOneField, atLeastOneFieldMessage);
/**
 * Schema for bulk-updating meeting event timestamps.
 *
 * Each item must include the timestamp `id` and at least one updatable field.
 */
export const meetingEventTimestampBulkUpdateSchema = z.object({
    timestamps: z.array(bulkUpdateItemSchema).min(1, 'At least one timestamp is required'),
});
/**
 * Schema for bulk-deleting meeting event timestamps.
 *
 * Accepts an array of timestamp IDs.
 */
export const meetingEventTimestampBulkDeleteSchema = z.object({
    ids: z
        .array(z.number().int().positive('Each ID must be a positive integer'))
        .min(1, 'At least one ID is required'),
});
/**
 * Schema for requesting AI-generated timestamp creation.
 *
 * Used by the POST `/generate` endpoint to trigger the agenda-timestamp-generation
 * queue job. When `confirmReplace` is true, existing AI-generated timestamps for
 * the same meeting event and media archive are replaced.
 *
 * Supports two agenda sources:
 * - `'system'` (default): uses the meeting event's system agenda sections.
 * - `'document'`: parses an uploaded PDF agenda outline identified by
 *   `meetingEventDocumentId`, which is required for this source.
 */
export const meetingEventTimestampGenerateSchema = z
    .object({
    mediaArchiveId: z.number().int().positive('Media archive ID is required'),
    confirmReplace: z.boolean().optional().default(false),
    agendaSource: z.enum(['system', 'document']).optional().default('system'),
    meetingEventDocumentId: z.number().int().positive().optional(),
})
    .superRefine((data, ctx) => {
    if (data.agendaSource === 'document' && !data.meetingEventDocumentId) {
        ctx.addIssue({
            code: 'custom',
            message: 'meetingEventDocumentId is required when agendaSource is document',
            path: ['meetingEventDocumentId'],
        });
    }
});
